// roots.js — surface roots that buttress out from the bonsai trunk base.
//
// One function, createRoots(tree), called by Tree.createTrunk() right after the
// trunk meshes are (re)built. It operates on the passed Tree: it reads the
// trunk's baseRadius / lean / trunkShape and adds the root meshes into
// tree.mesh (and tree._trunkParts so they're disposed on the next rebuild).
// Kept out of tree.js to keep that file focused on trunk/branch/foliage.

import * as THREE from 'three';
import { parameters, Math2, app } from './core.js';
import { applyRootBark, applyBirchBase } from './bark.js';

// The trunk's bark vertex colouring, applied to a root vertex at fraction t
// (0 = thick base, 1 = tip). Shared so Tree.updateTrunkColor can repaint roots
// live with the exact same formula they were built with.
export function rootVertexColor(baseColor, t) {
  const color = baseColor.clone();
  color.offsetHSL(0, 0, t * 0.15 - 0.1);
  color.offsetHSL((Math2.random() - 0.5) * 0.05, 0, (Math2.random() - 0.5) * 0.1);
  // Paper birch goes sooty right at the soil. Roots sit in the trunk's lowest band,
  // so map the root fraction (0 = trunk join, 1 = tip) into the bottom ~10% of the
  // trunk's height so the nebari matches the dark base and fades out toward the tips.
  applyBirchBase(color, t * 0.05);
  return [color.r, color.g, color.b];
}

// Build the tapered surface roots flaring from the trunk base. The root layout
// is rolled once into tree.trunkShape.roots and reused on every trunk rebuild,
// so the same tree keeps its roots when the trunk sliders reshape it.
export function createRoots(tree) {
  const baseRadius = tree.baseRadius;
  const { leanX, leanZ } = tree;

  // Roll a stable root layout once, reused on every rebuild.
  if (!tree.trunkShape.roots) {
    const count = 4 + Math.floor(Math2.random() * 3); // 4–6 roots
    const roots = [];
    for (let i = 0; i < count; i++) {
      // Spread evenly around the trunk with a little jitter so they aren't a fan.
      const angle = (i / count) * Math.PI * 2 + Math2.rangeRandom(-0.3, 0.3);
      roots.push({
        angle,
        dip: Math2.rangeRandom(0.45, 0.8),          // radians below horizontal
        lengthScale: Math2.rangeRandom(1.86, 2.86),   // × baseRadius
        radiusScale: Math2.rangeRandom(0.66, 0.86), // × baseRadius (thick end)
      });
    }
    tree.trunkShape.roots = roots;
  }

  const baseColor = new THREE.Color(parameters.truncColor);

  tree.trunkShape.roots.forEach(root => {
    const length = baseRadius * root.lengthScale;
    const rootRadius = baseRadius * root.radiusScale;

    // Tapered profile lathed around +Y: thick at the base, near-point at the tip.
    const profile = [];
    const profileSegments = 5;
    for (let i = 0; i <= profileSegments; i++) {
      const t = i / profileSegments;
      const r = Math.max(rootRadius * Math.pow(1 - t, 1.4), 0.12);
      profile.push(new THREE.Vector2(r, t * length));
    }
    const geometry = new THREE.LatheGeometry(profile, 6);

    // Roots on the obtuse side of a leaning trunk (the side away from the lean)
    // buttress out flatter against the soil, so flatten their dip toward
    // horizontal. The trunk leans toward (leanX, leanZ); a root pointing the
    // opposite way is most aligned with the obtuse side. Scale by how much the
    // trunk actually leans (leanMag, ~0 when upright → no change).
    const leanMag = Math.hypot(leanX, leanZ);
    let dip = root.dip;
    if (leanMag > 0.001) {
      const obtuseAngle = Math.atan2(-leanZ, -leanX);
      const align = Math.cos(root.angle - obtuseAngle); // 1 = fully obtuse side
      const leanNorm = Math.min(leanMag / Math.sin(Math.PI * 0.25), 1);
      dip *= 1 - 0.85 * Math.max(0, align) * leanNorm;
    }

    // Orient the +Y cone to point outward and down by `dip` from horizontal.
    const dir = new THREE.Vector3(
      Math.cos(root.angle) * Math.cos(dip),
      -Math.sin(dip),
      Math.sin(root.angle) * Math.cos(dip)
    ).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      dir
    );

    // Anchor the thick end well inside the trunk base so it merges with the
    // bark, lifted a touch up the trunk and offset by the trunk's lean there.
    // Then sink the base radially inward along the trunk axis so the cone's
    // base disc (and its top edge) is buried in the bark instead of poking out.
    // Flatter roots face their disc outward, so they need to sink deeper —
    // embed scales inversely with dip.
    const anchorY = baseRadius * 0.35;
    const embed = baseRadius * (0.55 - 0.4 * Math.sin(dip));
    const anchor = new THREE.Vector3(
      Math.cos(root.angle) * (baseRadius * 0.5 - embed) + leanX * anchorY * 0.8,
      anchorY,
      Math.sin(root.angle) * (baseRadius * 0.5 - embed) + leanZ * anchorY * 0.8
    );

    const pos = geometry.attributes.position;
    const v = new THREE.Vector3();
    const colors = [];
    const fracs = [];   // per-vertex t, kept so updateTrunkColor can repaint live
    for (let i = 0; i < pos.count; i++) {
      const localY = pos.getY(i);
      const t = localY / length;
      fracs.push(t);

      // Same low-poly bark noise the trunk uses, fading toward the tip.
      v.set(
        pos.getX(i) + (Math2.random() - 0.5) * parameters.truncNoise * (1 - t),
        localY,
        pos.getZ(i) + (Math2.random() - 0.5) * parameters.truncNoise * (1 - t)
      );
      v.applyQuaternion(quat).add(anchor);
      pos.setXYZ(i, v.x, v.y, v.z);

      // Match the trunk's vertex colouring (slightly darker toward the base).
      colors.push(...rootVertexColor(baseColor, t));
    }
    pos.needsUpdate = true;
    geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors), 3));
    geometry.computeVertexNormals();

    const material = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.8,
      metalness: 0.1,
      flatShading: true,
      // Clip the part of the root that dips below the soil so it can't show
      // through the pot walls/bottom (plane lives on app, see main.js).
      clippingPlanes: app.rootClipPlane ? [app.rootClipPlane] : [],
      clipShadows: true,
    });
    applyRootBark(material); // ancient bark normal at full strength (see setBarkTexture)

    const rootMesh = new THREE.Mesh(geometry, material);
    rootMesh.castShadow = true;
    rootMesh.receiveShadow = true;
    rootMesh.userData.isRoot = true;     // so updateTrunkColor repaints it as bark
    rootMesh.userData.rootFracs = fracs; // per-vertex t for that repaint

    tree.mesh.add(rootMesh);
    tree._trunkParts.push(rootMesh);
  });
}
